import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TicketSection } from './TicketSection';
import {
  addRow,
  EMPTY_HAZARD,
  removeRow,
  updateRow,
  type ActionHazard,
} from './actionTicketModel';

type HazardsSectionProps = {
  value: ActionHazard[];
  onChange: (rows: ActionHazard[]) => void;
};

/** Hazard / mitigation pairs, the ICS-204 safety block on a single task. */
export const HazardsSection: React.FC<HazardsSectionProps> = ({
  value,
  onChange,
}) => (
  <TicketSection
    title="Hazards & Risks"
    count={value.length || undefined}
    actions={
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange(addRow(value, { ...EMPTY_HAZARD }))}
      >
        <Plus /> Add row
      </Button>
    }
  >
    {value.length === 0 ? (
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        No hazards recorded yet.
      </p>
    ) : (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <div className="tw:hidden tw:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem] tw:gap-2 tw:text-xs tw:text-muted-foreground tw:sm:grid">
          <span>Hazard</span>
          <span>Mitigation</span>
          <span className="tw:sr-only">Remove</span>
        </div>
        {value.map((row, index) => (
          <div
            key={index}
            className="tw:grid tw:grid-cols-1 tw:items-center tw:gap-2 tw:sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem]"
          >
            <Input
              value={row.hazard}
              placeholder="e.g. Downed power lines"
              aria-label={`Hazard ${index + 1}`}
              onChange={(e) =>
                onChange(updateRow(value, index, { hazard: e.target.value }))
              }
            />
            <Input
              value={row.mitigation}
              placeholder="e.g. Coordinate with utility before entry"
              aria-label={`Mitigation ${index + 1}`}
              onChange={(e) =>
                onChange(
                  updateRow(value, index, { mitigation: e.target.value })
                )
              }
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove hazard ${index + 1}`}
              onClick={() => onChange(removeRow(value, index))}
            >
              <Trash2 />
            </Button>
          </div>
        ))}
      </div>
    )}
  </TicketSection>
);
